import { createContext, useContext, useMemo, type ReactNode } from 'react'
import {
  DEFAULT_MAP_STYLE_MODE,
  type MapStyleMode,
} from './map3DConfig'

export type MapStyleContextValue = {
  mapStyleMode: MapStyleMode
  onMapStyleModeChange: ((mode: MapStyleMode) => void) | undefined
}

const MapStyleContext = createContext<MapStyleContextValue | undefined>(undefined)

interface MapStyleProviderProps {
  mapStyleMode: MapStyleMode
  onMapStyleModeChange?: (mode: MapStyleMode) => void
  children: ReactNode
}

/**
 * Shared map style mode for main map, drawer maps, and in-chat map cards.
 * Lives under ChatMap (not the page shell) so shared chat components can sync
 * style without importing page-level contexts.
 */
function MapStyleProvider({
  mapStyleMode,
  onMapStyleModeChange,
  children,
}: MapStyleProviderProps) {
  const value = useMemo(
    () => ({ mapStyleMode, onMapStyleModeChange }),
    [mapStyleMode, onMapStyleModeChange],
  )

  return (
    <MapStyleContext.Provider value={value}>
      {children}
    </MapStyleContext.Provider>
  )
}

/** Soft read — falls back to the default style outside a provider (isolated tests). */
function useMapStyle(): MapStyleContextValue {
  const ctx = useContext(MapStyleContext)
  return {
    mapStyleMode: ctx?.mapStyleMode ?? DEFAULT_MAP_STYLE_MODE,
    onMapStyleModeChange: ctx?.onMapStyleModeChange,
  }
}

export {
  MapStyleProvider,
  useMapStyle,
}
